import Cookies from "js-cookie";
import { fetchAllUsers, listUsers } from "../../api/superAdminApi";
import { formatRoleLabel, parseUserRoles } from "../UserManagement/roleLabels";

export const ADMIN_ROLE_TOKENS = ["ADMIN#VI295", ";ADMIN#VI295"];

/** Roles a platform admin may assign — matches Super-Admin API allowed roles */
export const ASSIGNABLE_ROLES = [
    { value: "PERSONAL", group: "Messaging" },
    { value: "EMAIL", group: "Messaging" },
    { value: "EMAIL-ADMIN", group: "Messaging" },
    { value: "WB-ADMIN", group: "WhatsApp Business" },
    { value: "WB-USER", group: "WhatsApp Business" },
    { value: "WB-AGENT", group: "WhatsApp Business" },
    { value: "WB-CHAT", group: "WhatsApp Business" },
    { value: "CRM-ADMIN", group: "CRM" },
];

export const SECTION_ROLE_FILTER = {
    personal: "PERSONAL",
    email: "EMAIL",
};

const WB_ROLE_TOKENS = ["WB-ADMIN", "WB-USER", "WB-AGENT", "WB-CHAT"];

export const parseAdminRolesFromCookie = () =>
    (Cookies.get("userRole") || "")
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean);

export const isPlatformAdmin = (userRoleFromContext) => {
    const roles = [
        ...parseAdminRolesFromCookie(),
        ...(userRoleFromContext ? String(userRoleFromContext).split(",").map((r) => r.trim()) : []),
    ];
    return roles.some((r) => ADMIN_ROLE_TOKENS.includes(r));
};

export const rolesNeedWbConfig = (roleTokens) =>
    roleTokens.some((r) =>
        ["WB-ADMIN", "WB-USER", "WB-AGENT", "WB-CHAT"].includes(r)
    );

export const filterUsers = (users, { search = "", roleFilter = "ALL" }) => {
    const q = search.trim().toLowerCase();
    return users.filter((user) => {
        const matchesSearch =
            !q ||
            String(user.id).includes(q) ||
            (user.name || "").toLowerCase().includes(q) ||
            (user.email || "").toLowerCase().includes(q) ||
            (user.phone_number || "").toLowerCase().includes(q);

        if (!matchesSearch) return false;
        if (roleFilter === "ALL") return true;

        const tokens = parseUserRoles(user.user_role).map((r) => r.toUpperCase());
        return tokens.includes(roleFilter.toUpperCase());
    });
};

export const computeRoleStats = (users) => {
    const stats = { total: users.length };
    ASSIGNABLE_ROLES.forEach(({ value }) => {
        stats[value] = users.filter((u) =>
            parseUserRoles(u.user_role)
                .map((r) => r.toUpperCase())
                .includes(value)
        ).length;
    });
    return stats;
};

export const formatRoleChips = (userRoleStr) =>
    parseUserRoles(userRoleStr).map((token) => ({
        token,
        label: formatRoleLabel(token),
    }));

export const userHasRole = (user, roleToken) =>
    parseUserRoles(user?.user_role)
        .map((r) => r.toUpperCase())
        .includes(String(roleToken).toUpperCase());

export const userHasAnyRole = (user, roleTokens) =>
    roleTokens.some((t) => userHasRole(user, t));

export const filterUsersBySection = (users, section) => {
    switch (section) {
        case "personal":
            return users.filter((u) => userHasRole(u, "PERSONAL"));
        case "email":
            return users.filter((u) => userHasRole(u, "EMAIL"));
        case "whatsapp-business":
            return users.filter((u) => userHasAnyRole(u, WB_ROLE_TOKENS));
        default:
            return users;
    }
};

export const fetchAllAdminUsers = async (search = "") => {
    const params = search ? { search } : {};
    return fetchAllUsers(params);
};

export const fetchUsersForSection = async (section, search = "", page = 1, perPage = 200) => {
    const params = { page, per_page: perPage };
    if (search) params.search = search;

    if (section === "personal" || section === "email") {
        params.role = SECTION_ROLE_FILTER[section];
        const { users } = await listUsers(params);
        return { users, meta: {} };
    }

    if (section === "whatsapp-business") {
        const all = await fetchAllAdminUsers(search);
        const filtered = filterUsersBySection(all, "whatsapp-business");
        return { users: filtered, meta: { total: filtered.length } };
    }

    const { users, meta } = await listUsers(params);
    return { users, meta };
};

export const SECTION_META = {
    personal: {
        title: "Personal WhatsApp users",
        subtitle: "Manage Personal WA accounts, assign the PERSONAL role, and update PWC / DWPC / ADWPC credits.",
        defaultRoles: ["PERSONAL"],
        accent: "#1d4ed8",
    },
    email: {
        title: "Business email users",
        subtitle: "Manage email workers, domains, senders, BEC credits, and subscriptions.",
        defaultRoles: ["EMAIL"],
        accent: "#6d28d9",
    },
    "whatsapp-business": {
        title: "WhatsApp Business users",
        subtitle: "Manage WB-ADMIN, WB-USER, WB-AGENT, and WB-CHAT accounts with BWC credits.",
        defaultRoles: ["WB-USER"],
        accent: "#047857",
    },
    all: {
        title: "All users",
        subtitle: "Complete user directory across Personal WhatsApp, Email, WhatsApp Business, and CRM.",
        defaultRoles: [],
        accent: "#111827",
    },
};

export const SERVICE_LABELS = {
    personal_whatsapp: "Personal WhatsApp",
    email: "Business Email",
    whatsapp_business: "WhatsApp Business",
};

export const formatSubscriptionDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};
